import type { PortalBackInStockSubscriptionsDto } from '@supply-mover-2-0/shared';
import type { QueryClient } from '@tanstack/react-query';

/**
 * PD-14306 — React Query plumbing for the storefront's "Notify me" requests:
 * the list of items the signed-in customer is waiting on.
 *
 * The list is fetched once and shared by every out-of-stock control on the
 * page (a catalog page can show dozens), rather than a "requested" flag being
 * added to every catalog item response.
 */

/** Every cached list of requested items, for any customer and account. */
export const BACK_IN_STOCK_QUERY_KEY = ['back-in-stock'] as const;

/**
 * The key for one customer's list on one account.
 *
 * The account is part of it because a request belongs to the account the
 * customer was shopping as: the same item can be out of stock for one of
 * their accounts and available for another, so switching accounts must never
 * show the other account's requests.
 *
 * The customer is part of it because the query cache outlives a sign-out, and
 * two logins can share an account: the next person to sign in on the same
 * browser must not be shown the previous person's requests.
 */
export function backInStockQueryKey(
	publicUserId: number | null,
	activeClientId: number | null,
): ReadonlyArray<unknown> {
	return [...BACK_IN_STOCK_QUERY_KEY, publicUserId, activeClientId];
}

/**
 * How long the list is reused before it is fetched again. A request is
 * finished on the server when the customer is told, which is the only change
 * the storefront does not make itself, so the list does not need to be fresh
 * to the second.
 */
export const BACK_IN_STOCK_STALE_MS = 60_000;

/** Whether `itemId` is in the list. False while the list is not loaded. */
export function isBackInStockRequested(
	subscriptions: PortalBackInStockSubscriptionsDto | undefined,
	itemId: number,
): boolean {
	return subscriptions?.itemIds.includes(itemId) === true;
}

/**
 * The list with `itemId` added or removed, as a new object. Returns the same
 * object when nothing changes, so a cache write that changes nothing does not
 * re-render every control on the page.
 */
export function withBackInStockRequest(
	subscriptions: PortalBackInStockSubscriptionsDto | undefined,
	itemId: number,
	isRequested: boolean,
): PortalBackInStockSubscriptionsDto {
	const current = subscriptions ?? { itemIds: [] };
	const has = current.itemIds.includes(itemId);

	if (has === isRequested) {
		return current;
	}

	return {
		...current,
		itemIds: isRequested
			? [...current.itemIds, itemId]
			: current.itemIds.filter((id) => id !== itemId),
	};
}

/**
 * Write a saved request (or its cancellation) straight into one customer's
 * cached list for one account, so every control showing that item changes at
 * once instead of waiting for a refetch.
 */
export function applyBackInStockToCache(
	queryClient: QueryClient,
	publicUserId: number | null,
	activeClientId: number | null,
	itemId: number,
	isRequested: boolean,
): void {
	queryClient.setQueryData<PortalBackInStockSubscriptionsDto>(
		backInStockQueryKey(publicUserId, activeClientId),
		(old) => withBackInStockRequest(old, itemId, isRequested),
	);
}
